import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X, Save, BookOpen, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ChapterEditor
 *
 * Modal dialog that lets the promoter (or admin reviewer) browse every
 * chapter of the generated book and edit its markdown body.
 *
 * Two modes:
 *   - mode="edit" (default, promoter): saves directly via
 *     ``PUT /api/books/{id}/chapter`` and recomputes the PDF page count.
 *   - mode="propose" (admin reviewer in preparacion_imprenta): each
 *     "save" becomes a PROPOSED correction that the promoter must
 *     approve before the chapter content actually changes. Posts to
 *     ``POST /api/admin/books/{id}/propose-correction`` instead.
 *
 * Props:
 *   - open (bool)
 *   - onOpenChange (fn)
 *   - bookId (str)
 *   - initialHeading (str, optional) — opens the editor focused on this chapter
 *   - onSaved (fn, optional) — called after each successful save with the
 *     server response (shape differs per mode).
 *   - mode ("edit" | "propose"; default "edit")
 */
export default function ChapterEditor({ open, onOpenChange, bookId, initialHeading, onSaved, mode = 'edit' }) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeHeading, setActiveHeading] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [proposalNote, setProposalNote] = useState('');
  const isProposeMode = mode === 'propose';

  useEffect(() => {
    if (!open || !bookId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/books/${bookId}/chapters`, { withCredentials: true })
      .then((res) => {
        const list = res.data?.chapters || [];
        setChapters(list);
        const first = initialHeading && list.find(c => c.heading === initialHeading)
          ? initialHeading
          : (list[0]?.heading || null);
        setActiveHeading(first);
        const active = list.find(c => c.heading === first);
        setDraft(active?.body || '');
        setDirty(false);
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'No se pudo cargar el contenido.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookId, initialHeading]);

  const chooseChapter = (heading) => {
    if (dirty && !window.confirm('Tienes cambios sin guardar. ¿Descartarlos?')) return;
    setActiveHeading(heading);
    const ch = chapters.find(c => c.heading === heading);
    setDraft(ch?.body || '');
    setDirty(false);
  };

  const saveChapter = async () => {
    if (!activeHeading) return;
    setSaving(true);
    try {
      if (isProposeMode) {
        const res = await axios.post(
          `${API_URL}/api/admin/books/${bookId}/propose-correction`,
          { chapter_heading: activeHeading, proposed_body: draft, note: proposalNote.trim() },
          { withCredentials: true }
        );
        toast.success(`Propuesta enviada al promotor para el capítulo "${activeHeading}"`, { duration: 5000 });
        setDirty(false);
        setProposalNote('');
        onSaved?.(res.data);
      } else {
        const res = await axios.put(
          `${API_URL}/api/books/${bookId}/chapter`,
          { heading: activeHeading, body: draft },
          { withCredentials: true }
        );
        toast.success(`Capítulo "${activeHeading}" guardado (${res.data.word_count} palabras · libro: ${res.data.page_count ?? '?'} págs)`);
        setChapters(prev => prev.map(c => c.heading === activeHeading
          ? { ...c, body: draft, word_count: res.data.word_count }
          : c
        ));
        setDirty(false);
        onSaved?.(res.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || (isProposeMode
        ? 'No se pudo enviar la propuesta.'
        : 'No se pudo guardar el capítulo.');
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const wordCount = (draft || '').trim().split(/\s+/).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (!v && dirty && !window.confirm('Tienes cambios sin guardar. ¿Cerrar de todas formas?')) return;
      onOpenChange(v);
    }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="chapter-editor-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b bg-muted/30">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-primary" />
            {isProposeMode ? 'Proponer correcciones al promotor' : 'Editor de contenido del libro'}
          </DialogTitle>
          <DialogDescription>
            {isProposeMode
              ? 'Edita cualquier capítulo y envía la propuesta. El promotor recibirá un email y verá la corrección en su panel para aprobarla o rechazarla antes de que se envíe a imprenta.'
              : 'Ajusta el texto de cualquier capítulo. Los cambios se guardan al pulsar "Guardar capítulo" y el número de páginas se recalcula automáticamente.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 flex min-h-0 overflow-hidden">
            {/* Chapter list */}
            <aside className="w-64 border-r bg-muted/20 overflow-y-auto py-2" data-testid="chapter-editor-list">
              {chapters.map((c) => (
                <button
                  key={c.heading}
                  onClick={() => chooseChapter(c.heading)}
                  className={`w-full text-left px-3 py-2 text-sm border-l-2 transition-colors ${
                    activeHeading === c.heading
                      ? 'border-l-primary bg-primary/10 text-primary font-medium'
                      : 'border-l-transparent hover:bg-muted/50'
                  }`}
                  data-testid={`chapter-item-${c.heading}`}
                >
                  <div className="truncate">{c.heading}</div>
                  <div className="text-[10px] text-muted-foreground">{c.word_count} palabras</div>
                </button>
              ))}
            </aside>

            {/* Editor */}
            <main className="flex-1 flex flex-col min-w-0">
              <div className="px-4 py-2 border-b bg-white flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-sm font-medium">{activeHeading || '—'}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {wordCount} palabras {dirty && <span className="ml-2 text-amber-600">• cambios sin guardar</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`${API_URL}/api/books/${bookId}/full-pdf`, '_blank')}
                    className="gap-2"
                    data-testid="chapter-editor-pdf-btn"
                  >
                    <Download className="w-4 h-4" /> Ver / Descargar PDF
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveChapter}
                    disabled={saving || !dirty}
                    className="gap-2"
                    data-testid="save-chapter-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {isProposeMode ? 'Proponer corrección' : 'Guardar capítulo'}
                  </Button>
                </div>
              </div>
              {isProposeMode && (
                <div className="px-4 py-2 border-b bg-amber-50/40">
                  <label className="text-[11px] text-amber-900 font-medium block mb-1">
                    Nota para el promotor (opcional)
                  </label>
                  <input
                    type="text"
                    value={proposalNote}
                    onChange={(e) => setProposalNote(e.target.value)}
                    placeholder="Ej: ajuste de tono en el cierre del capítulo, corrección de erratas…"
                    className="w-full text-xs px-2 py-1.5 rounded border border-amber-200 bg-white focus:outline-none focus:border-amber-400"
                    data-testid="proposal-note-input"
                  />
                </div>
              )}
              <Textarea
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
                className="flex-1 resize-none font-mono text-sm leading-relaxed rounded-none border-0 focus-visible:ring-0"
                placeholder="Texto del capítulo…"
                data-testid="chapter-editor-textarea"
              />
            </main>
          </div>
        )}

        <DialogFooter className="px-5 py-3 border-t bg-muted/30">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-2">
            <X className="w-4 h-4" /> Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
